// src/utils/SoundManager.js

class SoundManager {
    constructor() {
        this.sounds = {};
        this.enabled = true;
        this.globalVolume = 0.7; // Default volume
    }

    // Preload a single sound
    preloadSound(name, path) {
        try {
            const audio = new Audio(path);
            audio.preload = 'auto';

            // Handle loading errors
            audio.addEventListener('error', (e) => {
                console.log(`Failed to load sound: ${name} from ${path}`, e);
            });

            audio.addEventListener('canplaythrough', () => {
                console.log(`✅ Sound loaded: ${name}`);
            });

            this.sounds[name] = audio;
        } catch (error) {
            console.log(`Error preloading sound ${name}:`, error);
        }
    }

    // Play a sound with optional volume override
    playSound(name, localVolume = 1.0) {
        if (!this.enabled || !this.sounds[name]) {
            if (!this.sounds[name]) {
                console.log(`Sound not found: ${name}`);
            }
            return;
        }

        try {
            const sound = this.sounds[name];
            sound.volume = this.globalVolume * localVolume;
            sound.currentTime = 0; // Reset to beginning

            // Create a promise to handle play()
            const playPromise = sound.play();

            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.log(`Sound play failed for ${name}:`, error);
                });
            }
        } catch (error) {
            console.log(`Error playing sound ${name}:`, error);
        }
    }

    // Stop a sound
    stopSound(name) {
        if (this.sounds[name]) {
            this.sounds[name].pause();
            this.sounds[name].currentTime = 0;
        }
    }

    // Toggle sound on/off
    toggleSound() {
        this.enabled = !this.enabled;
        console.log(`🔊 Sound ${this.enabled ? 'enabled' : 'disabled'}`);
        return this.enabled;
    }

    // Set global volume (0.0 to 1.0)
    setVolume(volume) {
        this.globalVolume = Math.max(0, Math.min(1, volume));
        console.log(`🔊 Volume set to: ${Math.round(this.globalVolume * 100)}%`);
    }

    // Initialize all sounds - using your actual WAV files
    initializeSounds() {
        console.log('🎵 Loading sounds...');

        // Advanced workout sounds
        this.preloadSound('advancedRep2', '/assets/sounds/advancedrep2.wav');
        this.preloadSound('advancedSet2', '/assets/sounds/advancedset2.wav');
        this.preloadSound('advancedSplash2', '/assets/sounds/advancedsplash2.wav');
        this.preloadSound('advancedStart2', '/assets/sounds/advancedstart2.wav');

        // Attack sounds
        this.preloadSound('attack', '/assets/sounds/attack.wav');

        // Bands workout sounds
        this.preloadSound('bandsRep2', '/assets/sounds/bandsrep2.wav');
        this.preloadSound('bandsSet2', '/assets/sounds/bandsset2.wav');
        this.preloadSound('bandsSplash2', '/assets/sounds/bandssplash2.wav');
        this.preloadSound('bandsStart2', '/assets/sounds/bandsstart2.wav');

        // Bodyweight workout sounds
        this.preloadSound('bodyweightRep2', '/assets/sounds/bodyweightrep2.wav');
        this.preloadSound('bodyweightSet2', '/assets/sounds/bodyweightset2.wav');
        this.preloadSound('bodyweightSplash', '/assets/sounds/bodyweightsplash.wav');
        this.preloadSound('bodyweightSplash2', '/assets/sounds/bodyweightsplash2.wav');
        this.preloadSound('bodyweightStart2', '/assets/sounds/bodyweightstart2.wav');

        // Defense sound
        this.preloadSound('defend', '/assets/sounds/defend.wav');

        // Everything mode sounds
        this.preloadSound('everythingMode2', '/assets/sounds/everythingmode2.wav');
        this.preloadSound('everythingRep2', '/assets/sounds/everythingrep2.wav');
        this.preloadSound('everythingSet2', '/assets/sounds/everythingset2.wav');
        this.preloadSound('everythingSplash2', '/assets/sounds/everythingsplash2.wav');
        this.preloadSound('everythingStart2', '/assets/sounds/everythingstart2.wav');

        // Game intro sounds
        this.preloadSound('gameIntroMode2', '/assets/sounds/gameintromode2.wav');

        // Navigation sounds
        this.preloadSound('next', '/assets/sounds/next.wav');
        this.preloadSound('next22', '/assets/sounds/next22.wav');

        // One more sound
        this.preloadSound('oneMore', '/assets/sounds/onemore.wav');
        this.preloadSound('oneMore2', '/assets/sounds/onemore2.wav');

        // Run sound
        this.preloadSound('run', '/assets/sounds/run.wav');

        // Strength workout sounds
        this.preloadSound('strengthRep2', '/assets/sounds/strengthrep2.wav');
        this.preloadSound('strengthSet2', '/assets/sounds/strengthset2.wav');
        this.preloadSound('strengthSplash2', '/assets/sounds/strengthsplash2.wav');
        this.preloadSound('strengthStart2', '/assets/sounds/strengthstart2.wav');

        // Villain sound
        this.preloadSound('villain', '/assets/sounds/villain.wav');

        // Workout complete sound
        this.preloadSound('workoutComplete2', '/assets/sounds/workoutcomplete2.wav');

        console.log('🎵 Sound loading initiated - ' + Object.keys(this.sounds).length + ' sounds');
    }

    // Play battle outcome sound based on result
    playBattleOutcome(outcomeMessage) {
        if (outcomeMessage.includes('CRUSHING VICTORY')) {
            this.playSound('workoutComplete2', 0.8);
        } else if (outcomeMessage.includes('DEVASTATING DEFEAT')) {
            this.playSound('villain', 0.8);
        } else if (outcomeMessage.includes('Victory') || outcomeMessage.includes('survived')) {
            this.playSound('oneMore2', 0.6);
        } else {
            this.playSound('villain', 0.6);
        }
    }

    // Play button sound (commonly used)
    playButtonSound() {
        this.playSound('next', 0.5);
    }

    // Play workout type specific sounds
    playWorkoutStartSound(workoutType) {
        switch(workoutType) {
            case 'bodyweight':
                this.playSound('bodyweightStart2', 0.7);
                break;
            case 'bands':
                this.playSound('bandsStart2', 0.7);
                break;
            case 'strength':
                this.playSound('strengthStart2', 0.7);
                break;
            case 'advanced':
                this.playSound('advancedStart2', 0.7);
                break;
            case 'everything':
                this.playSound('everythingStart2', 0.7);
                break;
            case 'battle':
                this.playSound('gameIntroMode2', 0.7);
                break;
            default:
                this.playSound('next', 0.7);
        }
    }

    // Play rep complete sound based on workout type
    playRepCompleteSound(workoutType) {
        switch(workoutType) {
            case 'bodyweight':
                this.playSound('bodyweightRep2', 0.6);
                break;
            case 'bands':
                this.playSound('bandsRep2', 0.6);
                break;
            case 'strength':
                this.playSound('strengthRep2', 0.6);
                break;
            case 'advanced':
                this.playSound('advancedRep2', 0.6);
                break;
            case 'everything':
                this.playSound('everythingRep2', 0.6);
                break;
            default:
                this.playSound('next22', 0.6);
        }
    }

    // Play set complete sound based on workout type
    playSetCompleteSound(workoutType) {
        switch(workoutType) {
            case 'bodyweight':
                this.playSound('bodyweightSet2', 0.7);
                break;
            case 'bands':
                this.playSound('bandsSet2', 0.7);
                break;
            case 'strength':
                this.playSound('strengthSet2', 0.7);
                break;
            case 'advanced':
                this.playSound('advancedSet2', 0.7);
                break;
            case 'everything':
                this.playSound('everythingSet2', 0.7);
                break;
            default:
                this.playSound('oneMore', 0.7);
        }
    }

    // Check if a sound is loaded
    isSoundLoaded(name) {
        return this.sounds[name] && this.sounds[name].readyState >= 2;
    }

    // Get all loaded sounds (for debugging)
    getLoadedSounds() {
        return Object.keys(this.sounds).filter(name => this.isSoundLoaded(name));
    }
}

// Create and export a global instance
export const soundManager = new SoundManager();

// Also export the class if needed elsewhere
export default SoundManager;